import DropZoneView from "./components/DropZoneView";
import {
  Concept,
  Pack,
  PackAttachment,
  PackAuthor,
  PackConfig,
  PackData,
  PackExtraData,
  PackLicense,
  PackMetadata,
  PackSource,
  RecursivePartial,
  Representation,
} from "./Types";
import {
  downloadPack,
  getObjectAsJson,
  getSerializedPack,
  getZipFromString,
  merge,
  uuid,
} from "./Lib";
import React, { Component, memo, useEffect } from "react";

import EditorView from "./components/EditorView";
import { print } from "./Printer";
import { WebRTCTransfer } from "./systems/WebRTCTransfer";
import { NetworkConnector } from "./components/NetworkConnector";
import memoizeOne from "async-memoize-one";
import {
  fileProcessor,
  handleJsZip,
  handleZip,
  OnFileEventFn,
} from "./FileProcessor";
import { Modal } from "./components/Modal";
import { HelpPage } from "./pages/HelpPage";
import { ProgressBar } from "./components/ProgressBar";
import { Licenses } from "./Licenses";
import { CreateOrImportPage } from "./pages/CreateOrImportPage";
import { AppState, PackEditorCallbacks } from "./core/EditorTypes";
import { AppContent } from "./AppContent";
import { stat } from "fs";

print("hello world");

let packChunks: string[] = [];

const generateRandomId = () => {
  return Math.random().toString(36).substring(2, 15);
};

const validateAndFixPack = (pack: Pack) => {
  // remove empty or invalid wires
  let fixedWires: string[] = [];
  for (const wire of pack.data.wiring) {
    let sep = "<>";
    let wireParts = wire.split("<>");
    if (wireParts.length !== 2) {
      sep = ">";
      wireParts = wire.split(">");
    }
    if (wireParts.length !== 2) {
      sep = "<";
      wireParts = wire.split("<");
    }

    if (wireParts.length !== 2) {
      continue;
    }

    const source = wireParts[0];
    const target = wireParts[1];

    if (source === target) {
      continue;
    }

    if (pack.data.reps[source] == null || pack.data.reps[target] == null) {
      continue;
    }

    fixedWires.push(`${source}${sep}${target}`);
  }

  // if we have no wires, wire up a bi-directional default
  if (fixedWires.length === 0) {
    const repKeys = Object.keys(pack.data.reps);
    if (repKeys.length >= 2) {
      fixedWires = [
        `${repKeys[0]}<>${repKeys[1]}`,
      ];
    }
  }

  return {
    ...pack,
    data: {
      ...pack.data,
      wiring: fixedWires,
    },
  };
};

class App extends Component<{}, AppState> {
  state: AppState = {
    // fileName: null,
    route: "index",
    packOriginal: null,
    packCurrent: null,
    packAttachments: [],
    uploading: false,
    networkState: "idle",
    importState: "idle",
    importProgress: 0,
    modal: "none",
    modalData: null,
    modalListeners: [],
  };
  createNewPack = () => {
    const pack: Pack = {
      metadata: {
        id: "my-pack-id",
        name: "My Pack Name",
        tags: ["#wip"],
        created: Number(new Date()),
        updated: Number(new Date()),
        version: "0.0.1",

        // need to update these
        contentType: "application/json",
        size: 0,
        url: "",
      },
      extra: {
        description: ["Short description of the pack."],
        attribution: {},
      },
      data: {
        reps: {
          "en": {
            "promptText": "English",
            "promptColor": "green",
          },
          "es": {
            "promptText": "español",
            "promptColor": "orange",
          },
        },
        config: {
          tts: {
            enabled: false,
            reps: {
              "en": {
                enabled: true,
                language: "en-US",
              },
              "es": {
                enabled: true,
                language: "es-ES",
              },
            },
          },
        },
        concepts: [
          {
            id: "brain",
            reps: {
              en: {
                text: "brain",
              },
              es: {
                text: "cerebro",
              },
            },
          },
          {
            id: "dolphin",
            reps: {
              en: {
                text: "dolphin",
              },
              es: {
                text: "delfín",
              },
            },
          },
          {
            id: "interesting",
            reps: {
              en: {
                text: "interesting",
              },
              es: {
                text: "interesante",
              },
            },
          },
          {
            id: "creativity",
            reps: {
              en: {
                text: "creativity",
              },
              es: {
                text: "creatividad",
              },
            },
          },
          {
            id: "science",
            reps: {
              en: {
                text: "science",
              },
              es: {
                text: "ciencia",
              },
            },
          },
        ],
        wiring: [
          "en<>es",
        ],
        templates: {
          "base_template": "Hello {{ name }}",
        },
        fields: {
          "name": "Carl Sagan",
        },
      },
    };
    this.setState({
      packOriginal: pack,
      packCurrent: { ...pack },
      route: "editor",
    });
  };
  onPackFile = (data, name, type) => {
    if (type === "application/json") {
      // conform
      if (!data.attribution) {
        data.attribution = {};
      }
      if (!data.extra) {
        data.extra = {};
      }
      const fixedPack = validateAndFixPack(data);
      this.setState({
        // fileName: file,
        packOriginal: fixedPack,
        packCurrent: { ...fixedPack },
        route: "editor",
      });
    } else {
      this.onPackAttachment({ data, name, type, id: uuid() });
    }
  };
  onPackAttachment = (packAttachment: PackAttachment) => {
    this.setState((state) => ({
      // Sorted and deduped
      packAttachments: [
        ...state.packAttachments.filter((x) => x.name != packAttachment.name),
        packAttachment,
      ].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  };
  updatePackAttachmentName = (attachmentId: string, newName: string) => {
    this.setState((state) => ({
      packAttachments: state.packAttachments.map((attachment) => {
        if (attachment.id === attachmentId) {
          return { ...attachment, name: newName };
        }
        return attachment;
      }),
    }));
  };
  removePackAttachment = (attachmentId: string) => {
    this.setState((state) => ({
      packAttachments: state.packAttachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
    }));
  };
  upload = async () => {
    console.log("requesting upload");
    WebRTCTransfer.requestSendPack(
      this.state.packCurrent!,
      await this.getSerializedPack(),
    );
    console.log("requested upload");
  };
  reset = () => {
    this.setState({
      // fileName: null,
      packOriginal: null,
      packCurrent: null,
      packAttachments: [],
      route: "index",
    });
    // remove downloadurl from url
    window.history.pushState(null, "", window.location.href.split("?")[0]);
  };
  save = () => {
    const fixed = validateAndFixPack(this.state.packCurrent!);
    downloadPack(fixed, this.state.packAttachments);
    // downloadObjectAsJson({ pack: this.state.packCurrent }, "pack");
  };
  updatePackField = (
    field: "id" | "name" | "description" | "version",
    value: unknown,
  ) => {
    if (field === "description") {
      this.setState((state) => ({
        packCurrent: {
          ...state.packCurrent!,
          metadata: {
            ...state.packCurrent!.metadata,
            updated: Number(new Date()),
          },
          extra: {
            ...state.packCurrent!.extra,
            [field]: value as string[],
          },
        },
      }));
    } else {
      this.setState((state) => ({
        packCurrent: {
          ...state.packCurrent!,
          metadata: {
            ...state.packCurrent!.metadata,
            [field]: value,
            updated: Number(new Date()),
          },
        },
      }));
    }
  };
  updateConceptId = (concept: Concept, newId: string) => {
    this.setState((state) => {
      const packNew: Pack = { ...state.packCurrent! };

      const existingConcept = packNew.data.concepts!.find((c) =>
        c.id === newId
      );
      if (existingConcept != null) {
        return null;
      }
      const newConcept = packNew.data.concepts!.find((c) =>
        c.id === concept.id
      );

      newConcept!.id = newId!;

      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  updateRepField = (
    concept: Concept | null,
    repKey: string,
    fieldKey: string,
    value: any,
  ) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      if (concept == null) {
        // Update base reps
        packNew.data.reps[repKey][fieldKey] = value;

        return {
          packCurrent: validateAndFixPack(packNew),
        };
      } else {
        const existingConcept = packNew.data.concepts!.find(
          (c) => c.id === concept.id,
        );
        const newConcept = {
          ...existingConcept!,
        };
        if (!newConcept.reps) {
          return null;
        }
        if (value && value !== "") {
          newConcept.reps[repKey][fieldKey] = value;
        } else if (newConcept.reps![repKey][fieldKey] != null) {
          delete newConcept.reps![repKey][fieldKey];
        }
        packNew.data
          .concepts[packNew.data.concepts!.indexOf(existingConcept!)] =
            newConcept;

        return {
          packCurrent: validateAndFixPack(packNew),
        };
      }
    });
  };
  removeConcept = (concept: Concept) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());

      console.log("removing concept", concept);
      packNew.data.concepts = packNew.data.concepts!.filter((c) =>
        c.id !== concept.id
      );
      // const index = packNew.data.concepts.indexOf(concept);
      // packNew.data.concepts.splice(index, 1);

      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  bump = () => {
    this.setState((state) => {
      return {
        packCurrent: this.bumpFor(state.packCurrent!),
      };
    });
  };
  bumpFor = (pack: Pack) => {
    // packNew.metadata.version = packNew.metadata.version + 1;
    pack.metadata.updated = Number(new Date());
    return pack;
  };
  addConcept = () => {
    this.setState(
      (state) => {
        const packNew = { ...state.packCurrent! };
        packNew.metadata.updated = Number(new Date());
        const prevConceptIndex = packNew.data.concepts.length - 1;
        const reps = prevConceptIndex >= 0
          ? Object.keys(packNew.data.concepts[prevConceptIndex].reps)
          : Object.keys(packNew.data.reps);
        const concept: Concept = {
          id: generateRandomId(),
          reps: {},
        };
        let index = 0;
        while (packNew.data.concepts.find((x) => x.id === concept.id)) {
          concept.id = generateRandomId();
        }

        for (let i = 0; i < reps.length; i++) {
          const key = reps[i];
          concept.reps[key] = {};
        }
        packNew.data.concepts.push(concept);

        return {
          packCurrent: validateAndFixPack(packNew),
        };
      },
      () => {
        setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 0);
      },
    );
  };
  setConcepts = (concepts: Concept[]) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      packNew.data.concepts = concepts;
      console.log("updating to be", packNew.data.concepts);
      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  addBaseRep = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const reps = Object.keys(packNew.data.reps);
      let repKey = "new_0";

      let index = 0;
      while (packNew.data.reps[repKey]) {
        repKey = `new_${++index}`;
      }

      packNew.data.reps[repKey] = {};

      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  updateBaseRepKey = (oldKey: string, newKey: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());

      // have to rebuild object to maintain order
      packNew.data.reps = Object.keys(packNew.data.reps)
        .reduce((a, key) => ({
          ...a,
          [key === oldKey ? newKey : key]: packNew.data.reps[key],
        }), {});

      for (let i = 0; i < packNew.data.concepts.length; i++) {
        const concept = packNew.data.concepts[i];
        if (concept.reps[oldKey] != null) {
          // have to rebuild object to maintain order
          concept.reps = Object.keys(concept.reps)
            .reduce((a, key) => ({
              ...a,
              [key === oldKey ? newKey : key]: concept.reps[key],
            }), {});
        }
      }

      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  removeBaseRep = (repKey: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());

      delete packNew.data.reps[repKey];

      for (let i = 0; i < packNew.data.concepts.length; i++) {
        const existingConcept = packNew.data.concepts[i]!;
        const newConcept = { ...existingConcept };
        if (newConcept.reps[repKey] != null) {
          delete newConcept.reps[repKey];
        }
        packNew.data.concepts[i] = newConcept;
      }
      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  addRepresentation = (concept: Concept, repKey: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const existingConcept = packNew.data.concepts!.find((x) =>
        x.id === concept.id
      )!;
      const newConcept = { ...existingConcept };

      // re-add to ensure sorted order
      const newRepKeys = Object.keys(newConcept!.reps);
      const newReps = {};
      const referenceReps = state.packCurrent!.data.reps;
      const referenceRepKeys = Object.keys(referenceReps);
      for (let i = 0; i < referenceRepKeys.length; i++) {
        const refKey = referenceRepKeys[i];
        if (refKey === repKey) {
          newReps[refKey] = {};
        } else if (newRepKeys.includes(refKey)) {
          newReps[refKey] = newConcept!.reps[refKey];
        } else {
          // doesn't currently contain, so don't add
        }
      }

      newConcept!.reps = { ...newReps };
      packNew.data.concepts[packNew.data.concepts!.indexOf(existingConcept)] =
        newConcept;

      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  removeRepresentation = (concept: Concept, repKey: string) => {
    // @ts-ignore idk
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const existingConcept = packNew.data.concepts.find((x) =>
        x.id === concept.id
      )!;
      const newConcept = { ...existingConcept };
      delete newConcept.reps![repKey];
      packNew.data.concepts[packNew.data.concepts.indexOf(existingConcept!)] =
        newConcept;
      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  addTemplate = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      let templateId = `template_0`;
      let index = 0;
      if (!packNew.data.templates) {
        packNew.data.templates = {};
      }
      while (
        Object.keys(packNew.data.templates).find((x) => x === templateId)
      ) {
        templateId = `template_${++index}`;
      }
      packNew.data.templates[templateId] = "enter your template here";
      return {
        packCurrent: packNew,
      };
    });
  };
  renameTemplate = (oldId: string, newId: string) => {
    if (oldId === newId) {
      return;
    }
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      if (!packNew.data.templates) {
        packNew.data.templates = {};
      }
      const newTemplates = {};
      const templateKeys = Object.keys(packNew.data.templates);
      for (let i = 0; i < templateKeys.length; i++) {
        const key = templateKeys[i];
        if (key === oldId) {
          newTemplates[newId] = packNew.data.templates[oldId];
        } else {
          newTemplates[key] = packNew.data.templates[key];
        }
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  editTemplate = (id: string, content: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      if (!packNew.data.templates) {
        packNew.data.templates = { [id]: content };
      } else {
        packNew.data.templates[id] = content;
      }
      console.log("updated template", packNew.data.templates);
      return {
        packCurrent: packNew,
      };
    });
  };
  removeTemplate = (templateId: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      console.log("remove template", templateId);
      delete packNew.data.templates![templateId];
      return {
        packCurrent: packNew,
      };
    });
  };
  addPackField = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      let fieldId = `field_0`;
      let index = 0;
      if (!packNew.data.fields) {
        packNew.data.fields = {};
      }
      while (Object.keys(packNew.data.fields).find((x) => x === fieldId)) {
        fieldId = `field_${++index}`;
      }
      packNew.data.fields[fieldId] = "enter text here";
      return {
        packCurrent: validateAndFixPack(packNew),
      };
    });
  };
  editPackField = (field: string, content: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      if (!packNew.data.fields) {
        packNew.data.fields = { [field]: content };
      } else {
        packNew.data.fields[field] = content;
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  renamePackField = (oldField: string, newField: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      if (!packNew.data.fields) {
        packNew.data.fields = { [newField]: "" };
      } else {
        packNew.data.fields[newField] = packNew.data.fields[oldField];
        delete packNew.data.fields[oldField];
      }
      // update every concept too
      for (let i = 0; i < packNew.data.concepts.length; i++) {
        const existingConcept = packNew.data.concepts[i]!;
        const newConcept = { ...existingConcept };
        if (!newConcept.fields) {
          continue;
        }
        if (newConcept.fields[oldField] == null) {
          continue;
        }
        newConcept.fields[newField] = newConcept.fields[oldField];
        delete newConcept.fields[oldField];
        packNew.data.concepts[i] = newConcept;
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  removePackField = (field: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      delete packNew.data.fields![field];
      return {
        packCurrent: packNew,
      };
    });
  };
  updateConceptField = (
    concept: Concept,
    fieldId: string,
    fieldValue: string,
  ) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const existingConcept = packNew.data.concepts!.find((x) =>
        x.id === concept.id
      )!;
      const newConcept = { ...existingConcept };
      if (!newConcept.fields) {
        newConcept.fields = {};
      }

      newConcept.fields[fieldId] = fieldValue;
      packNew.data.concepts[packNew.data.concepts!.indexOf(existingConcept)] =
        newConcept;
      return {
        packCurrent: packNew,
      };
    });
  };
  removeConceptField = (concept: Concept, fieldId: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const existingConcept = packNew.data.concepts!.find((x) =>
        x.id === concept.id
      )!;
      const newConcept = { ...existingConcept };
      delete newConcept.fields![fieldId];
      packNew.data.concepts[packNew.data.concepts!.indexOf(existingConcept)] =
        newConcept;
      return {
        packCurrent: packNew,
      };
    });
  };
  setWire = (wire: [string, string, string]) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.updated = Number(new Date());
      const currentWireIndex = packNew.data.wiring.findIndex((
        x,
      ) => ((x.startsWith(wire[0]) ||
        x.startsWith(wire[2])) && (x.endsWith(wire[0]) || x.endsWith(wire[2])))
      );
      if (currentWireIndex >= 0) {
        packNew.data.wiring.splice(currentWireIndex, 1);
      }
      if (wire[1] === "<") {
        packNew.data.wiring.push(`${wire[2]}>${wire[0]}`);
      } else if (wire[1] === ">") {
        packNew.data.wiring.push(`${wire[0]}>${wire[2]}`);
      } else if (wire[1] === "<>") {
        packNew.data.wiring.push(`${wire[0]}<>${wire[2]}`);
      }

      console.log(packNew.data.wiring)
      return {
        packCurrent: packNew,
      };
    });
  };
  saveAsLines = (key: string) => {
    console.log("saving as lines", key);
    const reps = this.state.packCurrent!.data.concepts.map((concept) => {
      return concept.reps[key]?.text;
    }).filter(Boolean);
    const lines = reps.join("\n");
    const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
    const saveAs = (blob: Blob, filename: string) => {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };
    saveAs(blob, `${this.state.packCurrent!.metadata.id}-${key}.txt`);
  };
  onConnected = () => {
    this.setState({
      networkState: "connected",
    });
  };
  onDisconnected = () => {
    this.setState({
      networkState: "idle",
    });
  };
  onNetworkMessage = async (message: any) => {
    const { type, payload } = message;
    if (type === "pack_request") {
      console.log(
        "got pack request",
        payload,
      );
      WebRTCTransfer.approve();
    }
    if (type === "pack_chunk") {
      const data = JSON.parse(payload);
      // console.log("got chunk", data);
      const chunk = data.content as string;
      packChunks.push(chunk);
      const isLast = data.index === data.count - 1;
      if (isLast) {
        const zip = await getZipFromString(packChunks.join(""));
        await handleJsZip(zip, this.onPackFile);
      }
    }
    if (
      type === "pack_approval" &&
      payload === "approve"
    ) {
      const serializedPack = await this.getSerializedPack();
      if (!serializedPack) {
        return;
      }
      this.setState({
        importState: "transferring",
      });
      WebRTCTransfer.sendPack(
        serializedPack,
        this.onSerializeProgress,
        () => {
          this.setState({
            importState: "idle",
          });
        },
      );
    }
  };
  onFileEvent: OnFileEventFn = async (event) => {
    const { type } = event;
    if (type === "import_start") {
      this.setState({
        importState: "importing",
      });
    }
    if (type === "import_progress") {
      this.setState({
        importProgress: event.progress,
      });
    }
    if (type === "import_end") {
      if (event.filetype === "apkg") {
        this.setState({
          importState: "idle",
          importProgress: 0,
        });
      } else {
        this.setState({
          importState: "idle",
          importProgress: 0,
        });
      }
    }
  };

  componentDidMount() {
    WebRTCTransfer.peer.subscribe("connected", this.onConnected);
    WebRTCTransfer.peer.subscribe("disconnected", this.onDisconnected);
    WebRTCTransfer.peer.subscribe("message", this.onNetworkMessage);

    this.checkForDownload();
  }
  async checkForDownload() {
    // Check for ?downloadurl=[x]
    const urlParams = new URLSearchParams(window.location.search);
    const downloadUrl = urlParams.get("downloadurl");
    if (downloadUrl) {
      const url = new URL(downloadUrl);
      console.log(url);

      // Start downloading file into memory
      const response = await fetch(url);
      const blob = await response.blob();

      const processFileSelectEvent = fileProcessor(
        this.onPackFile,
        this.onFileEvent,
        {
          onChooseDeck: this.onChooseDeck,
          onChooseColumns: this.onChooseColumns,
          isDirectFile: true,
        },
      );
      processFileSelectEvent(blob);
    }
  }

  componentWillUnmount() {
    WebRTCTransfer.peer.unsubscribe("connected", this.onConnected);
    WebRTCTransfer.peer.unsubscribe("disconnected", this.onDisconnected);
    WebRTCTransfer.peer.unsubscribe("message", this.onNetworkMessage);
  }
  onSerializeProgress = (progress: number) => {
    this.setState({
      importProgress: progress,
    });
  };
  // TODO Memoize
  getSerializedPackWithArgs = async (
    pack: Pack,
    attachments: PackAttachment[],
  ) => {
    this.setState({
      importState: "packaging",
      importProgress: 0,
    });
    console.log(
      "getSerializedPackWithArgs",
      pack.metadata.id,
      attachments.length,
    );
    const serializedPack = await getSerializedPack(
      pack,
      attachments,
      this.onSerializeProgress,
    );
    console.log("done", serializedPack.length);
    this.setState({
      importState: "idle",
    });
    return serializedPack;
  };
  getSerializedPack = () => {
    const { packCurrent, packAttachments } = this.state;
    if (!packCurrent) {
      return;
    }
    console.log("getSerializedPack");
    return this.getSerializedPackWithArgs(packCurrent, packAttachments);
  };
  alphabetize = (by: string | "{{ID}}", reverse: boolean) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };

      // Sort by rep or id
      if (by === "{{ID}}") {
        packNew.data.concepts = packNew.data.concepts.sort((a, b) => {
          return a.id.localeCompare(b.id);
        });
      } else {
        const repKey = by;
        // Use ID as tiebreaker if reps are equal/undefined
        packNew.data.concepts = packNew.data.concepts.sort((a, b) => {
          const aRep = a.reps[repKey].text;
          const bRep = b.reps[repKey].text;
          if (aRep === bRep) {
            return a.id.localeCompare(b.id);
          }
          if (!aRep) {
            return 1;
          }
          if (!bRep) {
            return -1;
          }
          return aRep.localeCompare(bRep);
        });
      }

      // Reverse if needed
      if (reverse) {
        packNew.data.concepts = packNew.data.concepts.reverse();
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  slice = (count: number | null) => {
    this.setState((state) => {
      if (!count) {
        return {
          packCurrent: state.packCurrent!,
          modal: "slice" as const,
          modalData: null,
        };
      }

      const packNew = { ...state.packCurrent! };
      packNew.data.concepts = packNew.data.concepts.slice(0, count);

      return {
        packCurrent: packNew,
        modal: "none",
        modalData: null,
      };
    });
  };
  convertHtmlToMd = () => {
    // data-original-markdown="(.*?)"
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.data.concepts = packNew.data.concepts.map((concept) => {
        for (let i = 0; i < Object.keys(concept.reps).length; i++) {
          const key = Object.keys(concept.reps)[i];
          const rep = concept.reps[key];
          if (rep.text) {
            const b64Text = /data-original-markdown="(.*?)"/.exec(rep.text)
              ?.[1];
            if (b64Text) {
              rep.text = atob(b64Text);
            }
            rep.text = rep.text.replaceAll(/<\/?div>/g, "\n").replaceAll(
              /<br\/?>/g,
              "\n",
            ).replaceAll("&gt;", ">").replaceAll("&lt;", "<").replaceAll(
              "&amp;",
              "&",
            ).replaceAll("&nbsp;", " ")
              .replaceAll("&quot;", '"');
          }
          concept.reps[key] = rep;
        }
        return concept;
      });
      return {
        packCurrent: packNew,
      };
    });
  };
  addLicense = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      if (!packNew.extra?.attribution?.license) {
        if (!packNew.extra?.attribution) {
          packNew.extra.attribution = {};
        }
        const shortname = "CC BY-SA 4.0";
        const license = Licenses[shortname];
        packNew.extra!.attribution!.license = {
          shortname,
          ...license,
        };
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  editLicense = (license: PackLicense) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra.attribution!.license = license;
      if (license.shortname !== "other") {
        const existingLicense = Licenses[license.shortname];
        packNew.extra.attribution!.license = {
          shortname: license.shortname,
          ...existingLicense,
        };
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  removeLicense = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      if (packNew.extra.attribution?.license) {
        delete packNew.extra.attribution!.license;
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  addAuthor = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      if (!packNew.extra.attribution) {
        packNew.extra.attribution = {
          authors: [],
        };
      }
      if (!packNew.extra.attribution.authors) {
        packNew.extra.attribution!.authors = [];
      }
      packNew.extra.attribution!.authors.push({
        name: "",
        type: "primary",
      });
      return {
        packCurrent: packNew,
      };
    });
  };
  editAuthor = (author: PackAuthor, index: number) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra!.attribution!.authors![index] = author;
      return {
        packCurrent: packNew,
      };
    });
  };
  removeAuthor = (index: number) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra!.attribution!.authors!.splice(index, 1);
      return {
        packCurrent: packNew,
      };
    });
  };
  addSource = () => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      if (!packNew.extra.attribution) {
        packNew.extra.attribution = {
          sources: [],
        };
      }
      if (!packNew.extra.attribution.sources) {
        packNew.extra.attribution!.sources = [];
      }

      packNew.extra.attribution!.sources.push({
        name: "",
        url: "",
      });

      return {
        packCurrent: packNew,
      };
    });
  };
  editSource = (source: PackSource, index: number) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra!.attribution!.sources![index] = source;
      return {
        packCurrent: packNew,
      };
    });
  };
  removeSource = (index: number) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra.attribution!.sources!.splice(index, 1);
      return {
        packCurrent: packNew,
      };
    });
  };
  addEmptyRep = (key: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      // add key to all concept reps
      for (let i = 0; i < packNew.data.concepts.length; i++) {
        const existingConcept = packNew.data.concepts[i]!;
        const newConcept = { ...existingConcept };
        newConcept.reps = { ...existingConcept.reps };
        if (!newConcept.reps[key]) {
          newConcept.reps[key] = {};
        }
        packNew.data.concepts[i] = newConcept;
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  addConceptForAllAttachments = () => {
    // go thru every attachment name and create a rep for it
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      for (let i = 0; i < state.packAttachments.length; i++) {
        const attachment = state.packAttachments[i]!;
        const keyUnprocessed = attachment.name;
        // remove extension
        const key = keyUnprocessed.split(".").slice(0, -1)[0];
        if (!key) {
          continue;
        }
        // add key to all concept reps
        if (packNew.data.concepts.find((c) => c.id === key)) {
          continue;
        }

        const newConcept = {
          id: key,
          reps: {},
        };

        packNew.data.concepts.push(newConcept);
      }
      return {
        packCurrent: this.bumpFor(packNew),
      };
    });
  };
  clearAllAttachments = () => {
    this.setState((state) => {
      return {
        packCurrent: this.bumpFor(state.packCurrent!),
        packAttachments: [],
      };
    });
  };
  fixConceptIds = () => {
    // replace all conceptIds that are like "new_0" with a random id
    const regex = /^new_\d+$/;
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      for (let i = 0; i < packNew.data.concepts.length; i++) {
        const concept = packNew.data.concepts[i]!;
        if (regex.test(concept.id)) {
          concept.id = generateRandomId();
        }
      }
      return {
        packCurrent: packNew,
      };
    });
  };
  modifyPackConfig = (config: RecursivePartial<PackConfig>) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.data.config = merge(packNew.data.config || {}, config);
      return {
        packCurrent: packNew,
      };
    });
  };
  modifyPackExtra = (extra: RecursivePartial<PackExtraData>) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.extra = merge(packNew.extra || {}, extra);
      return {
        packCurrent: packNew,
      };
    });
  };
  modifyPackTags = (tags: string[]) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };
      packNew.metadata.tags = tags;
      return {
        packCurrent: packNew,
      };
    });
  };
  onChooseDeck = async (decks: string[]): Promise<string> => {
    return new Promise(async (resolve) => {
      const selectDeckPromise = new Promise<string>((resolveInner) => {
        const updateSelectedDeck = (key: string, value: string) => {
          if (key !== "anki_choose_deck") {
            return;
          }
          resolveInner(value);
        };
        this.setState((state) => {
          return {
            modal: "anki_choose_deck",
            modalData: decks,
            modalListeners: state.modalListeners.concat(updateSelectedDeck),
          };
        });
      });
      try {
        const selectedDeck = await selectDeckPromise;
        this.setState({
          modal: "none",
        });
        resolve(selectedDeck);
      } catch (_) {
        this.setState({
          modal: "none",
        });
      }
    });
  };
  onChooseColumns = async (columns: string[]): Promise<number[]> => {
    return [1, 3];
  };
  setModal = (modal: AppState["modal"]) => {
    this.setState({ modal });
  };
  addLines2 = (reps: string[], repKey: string) => {
    this.setState((state) => {
      const packNew = { ...state.packCurrent! };

      // if a concept has id==reps[i], then skip, else, create new concept
      for (let i = 0; i < reps.length; i++) {
        const existingConcept = packNew.data.concepts
          .find(
            (c) => c.id === reps[i],
          );
        if (existingConcept) {
          continue;
        }
        const newConcept = {
          id: reps[i],
          reps: {
            [repKey]: {
              text: reps[i],
            },
          },
        };
        packNew.data.concepts.push(newConcept);
      }
      return {
        packCurrent: packNew,
        modal: "none",
      };
    });
  };
  notifyModalListeners = (key: string, value: any) => {
    this.state.modalListeners.forEach((listenerFn) => {
      listenerFn(key, value);
    });
  };
  render() {
    const packEditorCallbacks: PackEditorCallbacks = {
      updateConceptId: this.updateConceptId,
      onPackAttachment: this.onPackAttachment,
      updateRepField: this.updateRepField,
      addConceptCb: this.addConcept,
      setConceptsCb: this.setConcepts,
      removeConceptCb: this.removeConcept,
      addBaseRepCb: this.addBaseRep,
      addRepCb: this.addRepresentation,
      removeRepCb: this.removeRepresentation,
      updatePackField: this.updatePackField,
      editPackField: this.editPackField,
      updateBaseRepKey: this.updateBaseRepKey,
      updatePackAttachmentName: this.updatePackAttachmentName,
      removePackAttachment: this.removePackAttachment,
      removeBaseRepCb: this.removeBaseRep,
      setWire: this.setWire,
      modifyPackConfig: this.modifyPackConfig,
      modifyPackExtra: this.modifyPackExtra,
      addPackField: this.addPackField,
      removePackField: this.removePackField,
      renamePackField: this.renamePackField,
      addTemplate: this.addTemplate,
      editTemplate: this.editTemplate,
      removeTemplate: this.removeTemplate,
      updateConceptField: this.updateConceptField,
      removeConceptField: this.removeConceptField,
      renameTemplate: this.renameTemplate,
      addLicense: this.addLicense,
      editLicense: this.editLicense,
      removeLicense: this.removeLicense,
      addAuthor: this.addAuthor,
      editAuthor: this.editAuthor,
      removeAuthor: this.removeAuthor,
      addSource: this.addSource,
      editSource: this.editSource,
      removeSource: this.removeSource,
      modifyPackTags: this.modifyPackTags,
      createNewPack: this.createNewPack,
      onChooseColumns: this.onChooseColumns,
      onChooseDeck: this.onChooseDeck,
      onPackFile: this.onPackFile,
      onPackFileEvent: this.onFileEvent,

      upload: this.upload,
      save: this.save,
      reset: this.reset,

      setModal: this.setModal,

      addLines2: this.addLines2,
      addEmptyRep: this.addEmptyRep,
      saveAsLines: this.saveAsLines,
      convertHtmlToMd: this.convertHtmlToMd,
      alphabetize: this.alphabetize,
      notifyModalListeners: this.notifyModalListeners,
      slice: this.slice,
      addConceptForAllAttachments: this.addConceptForAllAttachments,
      clearAllAttachments: this.clearAllAttachments,
      fixConceptIds: this.fixConceptIds,
    };

    // @ts-ignore
    window.pack = this.state.packCurrent;
    // @ts-ignore
    window.save = this.save;

    return (
      <AppContent {...this.state} packEditorCallbacks={packEditorCallbacks} />
    );
  }
}

export default App;
